/** Rendering heuristic, not an accessibility certification: inspect the
 * silhouette as well as the ink so a dark crest with a white outline stays bare. */
export type LogoHalo = "light" | "dark" | null;
export type RGB = readonly [number, number, number];
export interface LogoPixels {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

function luminance(rgb: RGB): number {
  const linear = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * linear[0]! + 0.7152 * linear[1]! + 0.0722 * linear[2]!;
}
function contrast(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

export function logoHalo(image: LogoPixels, surface: RGB, light: RGB, dark: RGB): LogoHalo {
  const { data, width, height } = image;
  const bg = luminance(surface);
  const lightLum = luminance(light);
  const darkLum = luminance(dark);
  let ink = 0,
    lost = 0,
    edge = 0,
    lostEdge = 0,
    onLight = 0,
    onDark = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const alpha = (data[i + 3] ?? 0) / 255;
      // Ignore near-transparent padding and resampling noise.
      if (alpha < 0.2) continue;
      const rgb: RGB = [data[i]!, data[i + 1]!, data[i + 2]!];
      const painted: RGB = [
        rgb[0] * alpha + surface[0] * (1 - alpha),
        rgb[1] * alpha + surface[1] * (1 - alpha),
        rgb[2] * alpha + surface[2] * (1 - alpha),
      ];
      const poor = contrast(luminance(painted), bg) < 2.5;
      ink += alpha;
      if (poor) lost += alpha;
      const raw = luminance(rgb);
      onLight += contrast(raw, lightLum) * alpha;
      onDark += contrast(raw, darkLum) * alpha;
      const boundary =
        x === 0 ||
        y === 0 ||
        x === width - 1 ||
        y === height - 1 ||
        data[i - 4 + 3]! < 51 ||
        data[i + 4 + 3]! < 51 ||
        data[i - width * 4 + 3]! < 51 ||
        data[i + width * 4 + 3]! < 51;
      if (boundary) {
        edge += alpha;
        if (poor) lostEdge += alpha;
      }
    }
  }
  if (ink === 0 || edge === 0 || lost / ink < 0.45 || lostEdge / edge < 0.6) return null;
  return onLight >= onDark ? "light" : "dark";
}

// Downsample each decoded crest once; theme changes reuse its pixels.
const samples = new Map<string, LogoPixels>();
function sampleLogo(image: HTMLImageElement): LogoPixels | null {
  if (!image.complete || image.naturalWidth === 0) return null;
  const key = image.currentSrc || image.src;
  const cached = samples.get(key);
  if (cached) return cached;
  const scale = Math.min(1, 96 / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
  if (samples.size >= 32) samples.delete(samples.keys().next().value!);
  samples.set(key, pixels);
  return pixels;
}

/** Resolve the same theme tokens the card paints, including translucent glass.
 * Unsupported canvas / cross-origin images leave the original artwork intact. */
export function inspectLogo(
  image: HTMLImageElement,
  host: HTMLElement,
  surface: "card" | "band",
): LogoHalo {
  try {
    const pixels = sampleLogo(image);
    if (!pixels) return null;
    const styles = getComputedStyle(host);
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 1;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    const color = (...tokens: string[]): RGB => {
      ctx.clearRect(0, 0, 1, 1);
      for (const token of tokens) {
        ctx.fillStyle = styles.getPropertyValue(token).trim();
        ctx.fillRect(0, 0, 1, 1);
      }
      const d = ctx.getImageData(0, 0, 1, 1).data;
      return [d[0]!, d[1]!, d[2]!];
    };
    return logoHalo(
      pixels,
      color("--background", `--${surface}`),
      color("--band-foreground"),
      color("--band"),
    );
  } catch {
    // Reading an external image may taint canvas. Never hide or replace it.
    return null;
  }
}
